<!--
  Contenido del modal "Traer configuración de informe de otro proyecto".
  A un costado lista los proyectos de la misma empresa (accesibles) que ya tienen
  reports.detailed; al pulsar uno, carga su configuración y muestra una vista previa
  fiel (mismo motor render.js del constructor). "Seleccionar" emite las páginas para
  cargarlas en el Constructor (nada se persiste aquí). Emite `cancel` y `select`.
-->
<template>
  <div class="ifp">
    <!-- Lista de proyectos -->
    <aside class="ifp-side">
      <div class="ifp-side-h">{{ $t('projects') }} <span v-if="sources.length" class="ifp-count">{{ sources.length }}</span></div>
      <div v-if="loading" class="ifp-muted">{{ $t('loading') }}…</div>
      <div v-else-if="!sources.length" class="ifp-muted">{{ $t('no_projects_with_config') }}</div>
      <ul v-else class="ifp-list">
        <li
          v-for="p in sources"
          :key="p.id"
          class="ifp-item"
          :class="{ active: p.id === selectedId }"
          @click="selectProject(p)">
          {{ p.name }}
        </li>
      </ul>
    </aside>

    <!-- Columna 2: "Configuración" = qué páginas trae el proyecto (tachadas = ocultas) -->
    <section class="ifp-config">
      <div class="ifp-col-h">{{ $t('configuration') }}</div>
      <div v-if="!selectedId" class="ifp-muted">{{ $t('select_project_to_preview') }}</div>
      <div v-else-if="previewLoading" class="ifp-muted">{{ $t('loading') }}…</div>
      <ul v-else class="ifp-clist">
        <li v-for="(c, i) in configChips" :key="i" class="ifp-citem" :class="{ off: c.off }">
          <i v-if="c.off" class="fas fa-eye-slash" aria-hidden="true"></i>{{ c.label }}
        </li>
      </ul>
    </section>

    <!-- Columna 3: vista previa con la etiqueta de previsualización flotando por encima -->
    <section class="ifp-main">
      <div v-if="!selectedId" class="ifp-empty">{{ $t('select_project_to_preview') }}</div>
      <div v-else class="ifp-viewer">
        <div v-if="!previewLoading && previewPages.length" class="ifp-float">
          {{ $t('preview') }} · {{ sheetsCount }} {{ sheetsCount === 1 ? $t('page') : $t('pages') }}
        </div>
        <div class="ifp-scroll">
          <div v-if="previewLoading" class="ifp-empty">{{ $t('loading') }}…</div>
          <div v-else-if="!previewPages.length" class="ifp-empty">{{ $t('nothing_selected') }}</div>
          <div v-else class="rpb"><div class="rpb-preview" v-html="previewHtml"></div></div>
        </div>
      </div>
    </section>

    <!-- Pie de acciones -->
    <footer class="ifp-foot">
      <b-button size="is-small" @click="$emit('cancel')">{{ $t('cancel') }}</b-button>
      <b-button size="is-small" type="is-primary" class="ifp-btn-blue" :disabled="!canSelect" @click="confirm">{{ $t('select') }}</b-button>
    </footer>
  </div>
</template>

<script>
import { fetchReportConfigSources } from '·/components/activity-groups/api/index'
import { loadBuilderConfig } from '../../report-page-builder/services/page-builder.service'
import { ensureBasePages } from '../../report-page-builder/services/page-builder.converge'
import { DEFAULT_GROUPS, buildPreview, sheetsFor, discName, TYPES } from '../../report-page-builder/data/page-builder.render'

export default {
  name: 'import-from-project',

  props: {
    projectId: { type: [Number, String], default: null },
  },

  data() {
    return {
      loading: true,
      sources: [],          // [{ id, name }]
      selectedId: null,
      selectedName: '',
      previewPages: [],     // páginas (modelo builder) del proyecto elegido
      previewLoading: false,
      cache: {},            // projectId → páginas (evita refetch)
    }
  },

  computed: {
    ctx() {
      return {
        pages: this.previewPages,
        groups: DEFAULT_GROUPS,
        project: { name: this.selectedName, company: '', week: '' },
        s3bucket: (window.config && window.config.s3bucket) || '',
        coverPreview: '',
        activityGroups: [],
      }
    },
    previewHtml() { return this.previewPages.length ? buildPreview(this.ctx, 'full', null) : '' },
    // Resumen de la config: una "pastilla" por página (nombre; tachada si está oculta).
    configChips() {
      return (this.previewPages || []).map((p) => ({ label: this.displayName(p), off: p.visible === false }))
    },
    sheetsCount() {
      return this.previewPages.filter((p) => p.visible !== false)
        .reduce((n, p) => n + sheetsFor(p, this.ctx).length, 0)
    },
    canSelect() { return !!this.selectedId && !this.previewLoading && this.previewPages.length > 0 },
  },

  created() { this.load() },

  methods: {
    // Nombre visible de una página (la disciplina muestra su nombre de sección).
    displayName(p) {
      if (p.type === 'discipline') return (p.settings && p.settings.name) || discName(p.settings && p.settings.discipline)
      return (TYPES[p.type] && TYPES[p.type].name) || p.type
    },

    async load() {
      this.loading = true
      try {
        this.sources = await fetchReportConfigSources(this.projectId)
      } catch (e) {
        this.$buefy.toast.open({ message: this.$t('error_loading_data'), type: 'is-danger' })
      } finally {
        this.loading = false
      }
    },

    async selectProject(p) {
      this.selectedId = p.id
      this.selectedName = p.name
      if (this.cache[p.id]) { this.previewPages = this.cache[p.id]; return }
      this.previewLoading = true
      this.previewPages = []
      try {
        const { builder } = await loadBuilderConfig(p.id)
        // Completa las páginas base que el proyecto origen no tenga (portada/dashboard/
        // gráficos por grupo/tabla), para que siempre haya un informe usable por defecto.
        const pages = ensureBasePages((builder && builder.pages) ? builder.pages : [])
        this.cache[p.id] = pages
        if (this.selectedId === p.id) this.previewPages = pages
      } catch (e) {
        this.$buefy.toast.open({ message: this.$t('error_loading_data'), type: 'is-danger' })
      } finally {
        this.previewLoading = false
      }
    },

    confirm() {
      if (!this.canSelect) return
      // Copia profunda + portable: se suelta la imagen de portada y el groupId del origen
      // (ambos son por-proyecto). La portada y los grupos custom se resuelven en el Constructor.
      const pages = JSON.parse(JSON.stringify(this.previewPages))
      pages.forEach((p) => {
        if (p.type === 'cover') p.settings.image_cover = { type: 'file', name: null, mimeType: null, url: null, file: null }
        if (p.type === 'discipline') p.settings.groupId = null
      })
      this.$emit('select', { id: this.selectedId, name: this.selectedName, pages })
    },
  },
}
</script>

<style scoped>
.ifp {
  --ifp-line: #e2e7ef; --ifp-muted: #697289; --ifp-accent: #2456d6; --ifp-accent-wash: #e8eefc; --ifp-surface-2: #f6f8fb;
  display: grid; grid-template-columns: 240px 220px 1fr; grid-template-rows: minmax(0, 1fr) auto;
  gap: 16px; height: min(72vh, 700px);
}
.ifp-side { grid-row: 1 / 2; border: 1px solid var(--ifp-line); border-radius: 12px; overflow: hidden; display: flex; flex-direction: column; min-height: 0; }
.ifp-side-h { padding: 12px 14px; font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ifp-muted); border-bottom: 1px solid var(--ifp-line); display: flex; align-items: center; gap: 8px; }
.ifp-count { background: var(--ifp-surface-2); border-radius: 999px; padding: 1px 8px; font-size: 11px; }
.ifp-list { list-style: none; margin: 0; padding: 8px; overflow-y: auto; flex: 1; }
.ifp-item { padding: 9px 11px; border-radius: 8px; cursor: pointer; font-size: 13.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ifp-item:hover { background: var(--ifp-surface-2); }
.ifp-item.active { background: var(--ifp-accent-wash); color: var(--ifp-accent); }
.ifp-main { grid-row: 1 / 2; min-width: 0; display: flex; flex-direction: column; }
.ifp-muted { color: var(--ifp-muted); font-size: 12.5px; padding: 10px 14px; }
/* Columna "Configuración" (qué páginas trae el proyecto) — lista vertical */
.ifp-config { grid-row: 1 / 2; border: 1px solid var(--ifp-line); border-radius: 12px; overflow: hidden; display: flex; flex-direction: column; min-height: 0; }
.ifp-col-h { padding: 12px 14px; font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ifp-muted); border-bottom: 1px solid var(--ifp-line); }
.ifp-clist { list-style: none; margin: 0; padding: 8px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; gap: 5px; }
.ifp-citem { font-size: 11.5px; font-weight: 700; color: var(--ifp-accent); background: var(--ifp-accent-wash); border: 1px solid #d7e1fb; border-radius: 8px; padding: 5px 10px; display: flex; align-items: center; gap: 6px; }
.ifp-citem.off { color: var(--ifp-muted); background: var(--ifp-surface-2); border-color: var(--ifp-line); text-decoration: line-through; }
/* Visor con la etiqueta de previsualización flotando por encima */
.ifp-viewer { position: relative; flex: 1; min-height: 0; }
.ifp-float { position: absolute; top: 10px; right: 10px; z-index: 5; font-size: 11px; font-weight: 700; color: #fff; background: rgba(22, 32, 58, .82); border-radius: 999px; padding: 4px 11px; pointer-events: none; box-shadow: 0 2px 8px rgba(0, 0, 0, .18); }
.ifp-scroll { height: 100%; overflow-y: auto; background: var(--ifp-surface-2); border: 1px solid var(--ifp-line); border-radius: 12px; padding: 14px; }
.ifp-empty { color: #9aa3b5; font-size: 13px; text-align: center; padding: 48px 10px; }
.ifp-foot { grid-column: 1 / 4; grid-row: 2 / 3; display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid var(--ifp-line); padding-top: 14px; }
.ifp-foot .ifp-btn-blue { background: var(--ifp-accent); border-color: var(--ifp-accent); color: #fff; }
.ifp-foot .ifp-btn-blue:hover { background: #1b3d94; border-color: #1b3d94; }

@media (max-width: 980px) {
  .ifp { grid-template-columns: 1fr; grid-template-rows: auto auto 1fr auto; height: auto; }
  .ifp-side { grid-row: auto; max-height: 200px; }
  .ifp-config { grid-row: auto; max-height: 180px; }
  .ifp-main { grid-row: auto; }
}
</style>
